/**
 * TestBaseTemplate - Vista previa de la versión de prueba del BaseTemplate
 *
 * Solo accesible para admins. Muestra el BaseTemplate con la configuración
 * de la versión de prueba (base_template_test), sin afectar el sitio en vivo.
 */

import { BaseTemplate } from "@/components/templates/BaseTemplate";
import { isSameCloudinaryImage } from "@/lib/cloudinaryUtils";
import { brandDisplayNameFromSiteTitle } from "@/lib/siteBrandFromSettings";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useSEO } from "@/hooks/useSEO";
import { useTemplateMedia } from "@/hooks/useTemplateMedia";
import { getGalleryMedia, isGalleryConfigured } from "@/config/galleryConfig";
import { useMemo } from "react";
import { BRAND_SITE_URL, BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";

export default function TestBaseTemplate() {
  const { settings, isLoading } = useSiteSettings("test");
  const { showPortfolioNavLink } = usePortfolioSections();
  const useTemplateMediaApi = import.meta.env.VITE_USE_TEMPLATE_MEDIA_API === "true";
  const useEnvGallery = isGalleryConfigured();
  const envGalleryMedia = useMemo(() => getGalleryMedia(), []);
  const { media: apiMedia, loading: apiLoading, error: apiError } = useTemplateMedia(
    useTemplateMediaApi && !useEnvGallery
      ? { template_name: "BaseTemplate", is_active: true }
      : null
  );

  useSEO({
    title: `[Prueba] ${settings.title}`,
    description: settings.description,
    keywords: "",
    ogImage: BRAND_OG_IMAGE_URL,
    canonical: `${BRAND_SITE_URL}/test-base-template`,
    robots: "noindex, nofollow",
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando vista previa...</div>
      </div>
    );
  }

  const siteGalleryUrls = settings.galleryImageUrls?.filter((u) => typeof u === "string" && u.trim().length > 0) ?? [];
  const logoUrl = (settings.logoUrl as string)?.trim() || "";
  const galleryWithoutLogo = logoUrl
    ? siteGalleryUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : siteGalleryUrls;
  let finalMediaItems: Array<{ id: number; type: "video" | "photo"; src: string; title: string }>;
  let skipMuxFilter = false;

  if (galleryWithoutLogo.length > 0) {
    const galleryUrls = galleryWithoutLogo.length > 1 ? galleryWithoutLogo.slice(1) : galleryWithoutLogo;
    finalMediaItems = galleryUrls.map((src, i) => ({
      id: i + 1,
      type: "photo" as const,
      src: src.trim(),
      title: "",
    }));
    skipMuxFilter = true;
  } else if (useEnvGallery && envGalleryMedia.length > 0) {
    finalMediaItems = envGalleryMedia;
    skipMuxFilter = true;
  } else if (useTemplateMediaApi && !apiLoading && !apiError && apiMedia.length > 0) {
    finalMediaItems = apiMedia;
    skipMuxFilter = true;
  } else {
    finalMediaItems = [
      { id: 1, type: "photo" as const, src: "https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/9fdc1148-5ac5-48d3-2a3a-6ce149481300/public", title: "" },
    ];
    skipMuxFilter = true;
  }

  const bannerImageUrl =
    (typeof settings.bannerImageUrl === "string" && settings.bannerImageUrl.trim())
      ? settings.bannerImageUrl
      : galleryWithoutLogo.length > 0
        ? galleryWithoutLogo[0]
        : undefined;

  const brandName = brandDisplayNameFromSiteTitle(settings.title);

  return (
    <div className="relative">
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-4 py-2 bg-amber-500/95 text-black text-sm font-medium rounded-full shadow-lg">
        Vista previa de prueba — no es la versión oficial
      </div>
      <div className="pt-12">
        <BaseTemplate
          mediaItems={finalMediaItems}
          skipMuxApiFilter={skipMuxFilter}
          headerUrl={settings.headerUrl}
          logoUrl={settings.logoUrl}
          heroTitle={settings.title}
          heroDescription={settings.description}
          bannerImageUrl={bannerImageUrl}
          showPortfolioNavLink={showPortfolioNavLink}
          brandName={brandName}
          footerTagline={settings.footerTagline}
        />
      </div>
    </div>
  );
}
